/**
 * HTTP transport for the consumer API.
 *
 * Token model, and why it looks like this:
 *
 * - The **access token lives in memory only**. Never localStorage — an XSS there
 *   walks away with a credential that can hold money on a card.
 * - The **refresh token lives in an HttpOnly cookie**, which JS cannot read. The
 *   backend issues it only when the request announces itself with `X-Client-Type: web`
 *   (`wants_cookie_transport` in crates/api/src/routes/auth.rs); mobile clients keep
 *   their body-only contract. That header therefore goes on *every* request, not just
 *   login: it is what makes a session survive a reload at all.
 * - The body-supplied refresh token is still kept in memory as a fallback. It costs
 *   nothing (the server prefers the cookie whenever both arrive) and it covers the one
 *   real gap: `Secure` cookies over plain `http://localhost` in dev, which Safari
 *   refuses even though Chrome and Firefox treat localhost as a secure context.
 *
 * All paths are relative — the browser must see same-origin. The backend has no CORS
 * layer at all, and the refresh cookie is `SameSite=Strict`.
 */

/** Announces the web transport so the backend switches refresh to an HttpOnly cookie. */
const CLIENT_TYPE_HEADER = 'X-Client-Type';
const CLIENT_TYPE = 'web';

/**
 * A failed API call, carrying the backend's own error code.
 *
 * The wire shape is `{"error": {"code", "message"}}` — nested, not flat
 * (crates/api/src/error.rs, `IntoResponse for ApiError`). `code` is a stable machine
 * identifier (`duplicate_plate`, `account_locked`, …) and is what UI decisions branch
 * on; `message` is a Russian sentence written for the person on the other end and is
 * safe to show verbatim.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  /**
   * Extra fields the backend attached to the error envelope, when it has something structured to
   * say. Only `three_ds_required` uses this today — it carries what the browser must POST to the
   * payer's bank, and the request simply cannot continue without it.
   */
  readonly details: Record<string, unknown> | undefined;

  constructor(status: number, message: string, code?: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

let accessToken: string | null = null;
let refreshToken: string | null = null;

/** Notified whenever the session ends for a reason the user did not ask for. */
let onSessionLost: (() => void) | null = null;

export function setSessionLostHandler(handler: (() => void) | null): void {
  onSessionLost = handler;
}

/**
 * The backend's refusal to serve anybody who has not accepted the current legal redaction.
 *
 * It arrives on the order endpoints — create, add a car to a выезд, pay an overflow — as a 409.
 * The **code** is what is matched, never the status: 409 is also `duplicate_plate`,
 * `last_car_undeletable` and `active_order_exists_for_car`, and a status-first branch would
 * either swallow those or, worse, throw somebody into a legal screen for a duplicate plate.
 */
export const LEGAL_ACCEPTANCE_REQUIRED = 'legal_acceptance_required';

/** Notified when the server refuses a request until the current documents are accepted. */
let onLegalAcceptanceRequired: (() => void) | null = null;

export function setLegalAcceptanceRequiredHandler(handler: (() => void) | null): void {
  onLegalAcceptanceRequired = handler;
}

/**
 * Отказ сервера обслуживать сделку, пока адрес почты не подтверждён.
 *
 * Приходит с тех же эндпоинтов, что и `legal_acceptance_required`, и тем же 409 — и матчится так
 * же по КОДУ, а не по статусу: 409 на этих путях носят ещё `duplicate_plate`,
 * `last_car_undeletable` и `active_order_exists_for_car`, и ветка по статусу увела бы человека
 * подтверждать почту из-за задвоенного госномера.
 *
 * Почему заказ этим закрыт: кассовый чек уходит именно на этот адрес, и неподтверждённый означает
 * не «письмо не дошло», а «чек за оплаченный заказ уехал постороннему».
 */
export const EMAIL_NOT_VERIFIED = 'email_not_verified';

/** Notified when the server refuses a request until the email address is confirmed. */
let onEmailNotVerified: (() => void) | null = null;

export function setEmailNotVerifiedHandler(handler: (() => void) | null): void {
  onEmailNotVerified = handler;
}

export function setTokens(access: string | null, refresh?: string | null): void {
  accessToken = access;
  if (refresh !== undefined) refreshToken = refresh;
}

export function hasSession(): boolean {
  return accessToken !== null;
}

/**
 * The access token, for the one caller that cannot go through `apiRequest`.
 *
 * That caller is the SSE stream. It is read with `fetch` rather than `EventSource` precisely
 * because it must send this header, and it holds one long-lived response open instead of making
 * discrete requests. Nothing else should reach for this: `apiRequest` attaches the header,
 * refreshes on 401 and retries, and this getter does none of that.
 */
export function getAccessToken(): string | null {
  return accessToken;
}

/** Endpoints that must never trigger the refresh-and-retry path (they ARE the auth path). */
const AUTH_PATHS = [
  '/v1/auth/consumer/login',
  '/v1/auth/consumer/register',
  '/v1/auth/refresh',
  '/v1/auth/logout',
  '/v1/auth/logout-all',
  '/v1/auth/password-reset/request',
  '/v1/auth/password-reset/confirm',
  // Passkey sign-in. A 401 here means the assertion did not verify — there is no session to
  // refresh, and retrying would re-send an assertion whose challenge the server has already
  // consumed, turning one honest failure into two.
  '/v1/auth/consumer/webauthn/login-options',
  '/v1/auth/consumer/webauthn/login',
];

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  /** Internal: prevents an infinite refresh loop on a retried request. */
  retried?: boolean;
}

async function parseError(res: Response): Promise<ApiError> {
  try {
    const data: unknown = await res.json();
    const envelope = (data as { error?: unknown }).error;
    if (envelope !== null && typeof envelope === 'object') {
      const { code, message, ...rest } = envelope as {
        code?: string;
        message?: string;
        [key: string]: unknown;
      };
      if (typeof message === 'string' && message.length > 0) {
        return new ApiError(res.status, message, code, rest);
      }
      if (typeof code === 'string') {
        return new ApiError(res.status, `Ошибка ${res.status}`, code, rest);
      }
    }
    // Some middleware layers answer before the handler and use a plain string body.
    if (typeof envelope === 'string') return new ApiError(res.status, envelope);
  } catch {
    // A non-JSON body usually means an error page from something in front of the API.
  }
  return new ApiError(res.status, `Ошибка ${res.status}`);
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, retried = false } = options;

  const headers: Record<string, string> = { [CLIENT_TYPE_HEADER]: CLIENT_TYPE };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      credentials: 'include',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    // fetch() rejects only on a transport-level failure — DNS, TLS, offline, aborted
    // connection. An HTTP error status is a resolved promise and is handled below.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(0, 'Нет связи с сервером', 'network_error');
  }

  if (res.status === 401 && !retried && !AUTH_PATHS.includes(path)) {
    const failure = await parseError(res);
    // Only an expired or malformed TOKEN is worth refreshing for. A 401 can also mean the
    // request itself was refused — `POST /v1/consumer/me/password` answers
    // `invalid_credentials` for a mistyped current password — and refreshing then replaying it
    // would submit the same wrong password twice, spending two attempts of the 5-per-15-minutes
    // budget on one typo and moving the account closer to a 24-hour lockout.
    if (failure.code !== undefined && failure.code !== 'token_invalid') throw failure;

    const refreshed = await refreshSession();
    if (refreshed) {
      return apiRequest<T>(path, { ...options, retried: true });
    }
    setTokens(null, null);
    onSessionLost?.();
    throw new ApiError(401, 'Сессия истекла, войдите заново', 'unauthorized');
  }

  if (!res.ok) {
    const failure = await parseError(res);
    // Code first, status second — and here the status is not consulted at all. The gate has to
    // fire on `legal_acceptance_required` wherever the backend chooses to send it; today that is
    // a 409 from the order endpoints, and a handler keyed to 409 would silently stop working the
    // day one of them answers 403 instead.
    if (failure.code === LEGAL_ACCEPTANCE_REQUIRED) onLegalAcceptanceRequired?.();
    if (failure.code === EMAIL_NOT_VERIFIED) onEmailNotVerified?.();
    throw failure;
  }

  return (await readBody<T>(res)) as T;
}

/**
 * The response body, when there is one.
 *
 * `res.json()` on an empty body throws a bare `SyntaxError`, and several endpoints answer 200
 * with nothing at all — `password-reset/request` and `notifications/read-all` among them. Left
 * alone, that turns every successful reset request into what looks like a failure.
 *
 * The same path covers the other shape this app is exposed to: a 200 carrying HTML. Its own SPA
 * fallback answers unknown paths with `index.html`, and captive portals do it everywhere. That
 * has to read as a retryable transport problem, not as a session that ended.
 */
async function readBody<T>(res: Response): Promise<T | undefined> {
  if (res.status === 204) return undefined;

  const text = await res.text();
  if (text.trim().length === 0) return undefined;

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError(res.status, 'Сервер ответил неожиданным образом', 'malformed_response');
  }
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user_id: string;
  role: string;
  /**
   * Секрет доверенного устройства — только при входе по паролю с устройства, которое своего
   * секрета не показало. Хранится в `entities/session/deviceToken.ts` и предъявляется при
   * следующем входе; устройству, вошедшему со своим секретом, поле не приходит.
   */
  device_token?: string;
}

/**
 * Exchange the refresh token for a new access token.
 *
 * Returns false rather than throwing: every caller wants "could we resume, yes or no",
 * and an expired session is the normal case on a cold start, not an error worth surfacing.
 *
 * The body token is sent only as the dev-Safari fallback described at the top of the file.
 * When the cookie is present the server ignores the body and — importantly — rotates the
 * cookie in the response, so the browser's copy never goes stale and reuse-detection
 * never fires on our own refresh.
 */
let inFlightRefresh: Promise<boolean> | null = null;

export async function refreshSession(): Promise<boolean> {
  // One rotation at a time, always. The home screen fires three requests at once, and if the
  // access token has expired they all come back 401 together — three concurrent refreshes with
  // the same token means two of them present a token that has already been rotated. The server
  // reads that as replay (RFC 6819 §5.2.2.3), revokes the whole family, and the person is
  // signed out for doing nothing but opening the app.
  if (inFlightRefresh !== null) return inFlightRefresh;

  inFlightRefresh = (async () => {
    try {
      const data = await apiRequest<LoginResponse>('/v1/auth/refresh', {
        method: 'POST',
        body: refreshToken ? { refresh_token: refreshToken } : {},
      });
      setTokens(data.access_token, data.refresh_token);
      return true;
    } catch {
      return false;
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}
